import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  usersApi,
  interviewsApi,
  assessmentsApi,
  auditApi,
  invigilationApi,
  sectionsApi,
  UserProfile,
  CandidateAssignmentSummary,
  Interview,
  InterviewQuestion,
  Assessment,
  CandidateSection
} from '../lib/api';

interface Candidate extends UserProfile {
  interview_id?: string;
  interview_scheduled_at?: string;
  assignment_status?: string;
  interview_type?: 'text' | 'voice' | 'mcq' | string;
  candidate_can_view_results?: boolean;
  auto_approve_assessment?: boolean;
  review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
  restriction_enabled?: boolean;
  allowed_ip_ranges?: string[];
  allowed_locations?: Array<{
    raw_input: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    normalized_tokens?: string[];
  }>;
  match_mode?: 'any' | 'all' | string;
  enforcement_mode?: 'start_only' | 'continuous' | string;
  fail_action?: 'block' | 'warn' | string;
  allow_retake?: boolean;
  max_attempts?: number;
  attempt_count?: number;
  interview_title?: string;
}

interface CreateCandidateResult {
  user: UserProfile;
  invitationToken?: string;
  invitationExpiry?: string;
  invitation_link?: string;
}

const ASSIGNMENT_STATUS_PRIORITY: Record<string, number> = {
  in_progress: 0,
  scheduled: 1,
  pending: 2,
  completed: 3
};

function sortCandidateAssignmentSummaries(
  assignments: CandidateAssignmentSummary[]
): CandidateAssignmentSummary[] {
  return [...assignments].sort((left, right) => {
    const statusDelta = (ASSIGNMENT_STATUS_PRIORITY[left.assignment_status] ?? 99)
      - (ASSIGNMENT_STATUS_PRIORITY[right.assignment_status] ?? 99);

    if (statusDelta !== 0) {
      return statusDelta;
    }

    const leftScheduled = left.interview_scheduled_at ? new Date(left.interview_scheduled_at).getTime() : Number.POSITIVE_INFINITY;
    const rightScheduled = right.interview_scheduled_at ? new Date(right.interview_scheduled_at).getTime() : Number.POSITIVE_INFINITY;
    if (leftScheduled !== rightScheduled) {
      return leftScheduled - rightScheduled;
    }

    const leftUpdated = left.assignment_updated_at ? new Date(left.assignment_updated_at).getTime() : 0;
    const rightUpdated = right.assignment_updated_at ? new Date(right.assignment_updated_at).getTime() : 0;
    return rightUpdated - leftUpdated;
  });
}

function normalizeCandidateAssignments(candidate: Candidate): Candidate {
  const explicitAssignments = Array.isArray(candidate.candidate_assignments)
    ? candidate.candidate_assignments.filter((assignment) => assignment && assignment.assignment_status !== 'cancelled')
    : [];

  const fallbackAssignment: CandidateAssignmentSummary[] = (
    explicitAssignments.length === 0 && candidate.interview_id
      ? [{
          id: `${candidate.id}:${candidate.interview_id}`,
          candidate_id: candidate.id,
          interview_id: candidate.interview_id,
          interview_scheduled_at: candidate.interview_scheduled_at,
          interview_completion_deadline: candidate.interview_completion_deadline,
          assignment_status: candidate.assignment_status || 'scheduled',
          interview_type: candidate.interview_type,
          candidate_can_view_results: candidate.candidate_can_view_results,
          auto_approve_assessment: candidate.auto_approve_assessment,
          review_mode: candidate.review_mode,
          restriction_enabled: candidate.restriction_enabled,
          allowed_ip_ranges: candidate.allowed_ip_ranges,
          allowed_locations: candidate.allowed_locations,
          match_mode: candidate.match_mode,
          enforcement_mode: candidate.enforcement_mode,
          fail_action: candidate.fail_action,
          allow_retake: candidate.allow_retake,
          max_attempts: candidate.max_attempts,
          attempt_count: candidate.attempt_count,
          assignment_updated_at: candidate.assignment_updated_at,
          interview_title: candidate.interview_title
        }]
      : []
  );

  const mergedAssignments = sortCandidateAssignmentSummaries(
    explicitAssignments.length > 0 ? explicitAssignments : fallbackAssignment
  );

  const primaryAssignment = mergedAssignments[0];

  return {
    ...candidate,
    candidate_assignments: mergedAssignments,
    interview_id: primaryAssignment?.interview_id,
    interview_scheduled_at: primaryAssignment?.interview_scheduled_at,
    interview_completion_deadline: primaryAssignment?.interview_completion_deadline,
    assignment_status: primaryAssignment?.assignment_status,
    interview_type: primaryAssignment?.interview_type,
    candidate_can_view_results: primaryAssignment?.candidate_can_view_results,
    auto_approve_assessment: primaryAssignment?.auto_approve_assessment,
    review_mode: primaryAssignment?.review_mode,
    proctoring_enabled: primaryAssignment?.proctoring_enabled,
    proctoring_config: primaryAssignment?.proctoring_config,
    proctoring_require_consent: primaryAssignment?.proctoring_require_consent,
    proctoring_evidence_mode: primaryAssignment?.proctoring_evidence_mode,
    restriction_enabled: primaryAssignment?.restriction_enabled,
    allowed_ip_ranges: primaryAssignment?.allowed_ip_ranges,
    allowed_locations: primaryAssignment?.allowed_locations,
    match_mode: primaryAssignment?.match_mode,
    enforcement_mode: primaryAssignment?.enforcement_mode,
    fail_action: primaryAssignment?.fail_action,
    allow_retake: primaryAssignment?.allow_retake,
    max_attempts: primaryAssignment?.max_attempts,
    attempt_count: primaryAssignment?.attempt_count,
    assignment_updated_at: primaryAssignment?.assignment_updated_at,
    interview_title: primaryAssignment?.interview_title
  };
}

interface AdminContextType {
  interviews: Interview[];
  candidates: Candidate[];
  invigilators: UserProfile[];
  sections: CandidateSection[];
  assessments: Assessment[];
  loading: boolean;
  createInterview: (data: Partial<Interview> & { questions?: Partial<InterviewQuestion>[] }) => Promise<string>;
  createCandidate: (data: Partial<Candidate>) => Promise<CreateCandidateResult>;
  createInvigilator: (data: Partial<UserProfile>) => Promise<CreateCandidateResult>;
  assignInterview: (
    candidateId: string,
    interviewId: string,
    scheduledAt: string,
    interviewType?: 'text' | 'voice' | 'mcq',
    allowRetake?: boolean,
    maxAttempts?: number,
    completionDeadline?: string | null,
    candidateCanViewResults?: boolean,
    autoApproveAssessment?: boolean,
    accessPolicy?: {
      restriction_enabled?: boolean;
      allowed_ip_ranges?: string[];
      allowed_locations?: Array<{
        raw_input: string;
        city?: string | null;
        region?: string | null;
        country?: string | null;
        normalized_tokens?: string[];
      }>;
      match_mode?: 'any' | 'all';
      enforcement_mode?: 'start_only' | 'continuous';
      fail_action?: 'block' | 'warn';
    },
    proctoringPolicy?: {
      proctoring_enabled?: boolean;
      proctoring_config?: Record<string, any>;
    }
  ) => Promise<void>;
  cancelInterviewAssignment: (candidateId: string, assignmentId: string) => Promise<void>;
  assignInvigilator: (
    invigilatorId: string,
    candidateIds: string[],
    interviewId: string
  ) => Promise<{ addedCount: number; skippedCandidates: string[] }>;
  assignInvigilatorBySection: (
    invigilatorId: string,
    sectionId: string,
    interviewId: string
  ) => Promise<{ addedCount: number; skippedCandidates: string[] }>;
  createSection: (data: { name: string; description?: string }) => Promise<void>;
  updateSection: (sectionId: string, data: { name?: string; description?: string; status?: 'active' | 'archived' }) => Promise<void>;
  deleteSection: (sectionId: string, options?: { archive?: boolean }) => Promise<void>;
  refreshSections: (options?: { includeArchived?: boolean }) => Promise<void>;
  addSectionMembers: (sectionId: string, candidateIds: string[]) => Promise<{ added_count: number; candidates: string[] }>;
  removeSectionMember: (sectionId: string, candidateId: string) => Promise<void>;
  assignInterviewToSection: (sectionId: string, data: {
    interviewId: string;
    scheduledAt?: string | null;
    completionDeadline?: string | null;
    candidateCanViewResults?: boolean;
    autoApproveAssessment?: boolean;
    restrictionEnabled?: boolean;
    allowedIpRanges?: string[];
    allowedLocations?: Array<{
      raw_input: string;
      city?: string | null;
      region?: string | null;
      country?: string | null;
      normalized_tokens?: string[];
    }>;
    matchMode?: 'any' | 'all';
    enforcementMode?: 'start_only' | 'continuous';
    failAction?: 'block' | 'warn';
    interviewType?: 'text' | 'voice' | 'mcq';
    allowRetake?: boolean;
    maxAttempts?: number;
    retakeScope?: 'none' | 'part_a' | 'part_b' | 'both';
    reviewMode?: 'manual' | 'auto_approve' | 'auto_review';
  }) => Promise<void>;
  removeSectionAssignment: (sectionId: string, assignmentId: string) => Promise<void>;
  approveAssessment: (
    assessmentId: string,
    approved: boolean,
    modifiedScore?: number,
    comments?: string,
    scoreModificationReason?: string
  ) => Promise<void>;
  loadData: () => Promise<void>;
  deleteInterview: (id: string) => Promise<void>;
  updateInterview: (id: string, data: Partial<Interview>) => Promise<void>;
  deleteCandidate: (id: string) => Promise<void>;
  deleteInvigilator: (id: string) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

async function safeAuditLog(...args: Parameters<typeof auditApi.log>) {
  try {
    await auditApi.log(...args);
  } catch (error) {
    console.warn('[AdminContext] Audit log failed:', error);
  }
}

export function AdminProvider({ children }: AdminProviderProps) {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [invigilators, setInvigilators] = useState<UserProfile[]>([]);
  const [sections, setSections] = useState<CandidateSection[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshSections = useCallback(async (options?: { includeArchived?: boolean }) => {
    const sectionsRes = await sectionsApi.list(
      options?.includeArchived ? { include_archived: true } : undefined
    );
    setSections(sectionsRes.sections || []);
  }, []);

  const refreshUsers = useCallback(async () => {
    const [candidateRes, invigilatorRes] = await Promise.all([
      usersApi.listAll({ role: 'candidate' }),
      usersApi.listAll({ role: 'invigilator' })
    ]);

    setCandidates((candidateRes.users || []).map((candidate) => normalizeCandidateAssignments(candidate as Candidate)));
    setInvigilators(invigilatorRes.users || []);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [interviewsRes, candidateRes, invigilatorRes, assessmentsRes, sectionsRes] = await Promise.all([
        interviewsApi.list(),
        usersApi.listAll({ role: 'candidate' }),
        usersApi.listAll({ role: 'invigilator' }),
        assessmentsApi.list(),
        sectionsApi.list()
      ]);

      setInterviews(interviewsRes.interviews || []);
      setCandidates((candidateRes.users || []).map((candidate) => normalizeCandidateAssignments(candidate as Candidate)));
      setInvigilators(invigilatorRes.users || []);
      setAssessments(assessmentsRes.assessments || []);
      setSections(sectionsRes.sections || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createInterview = useCallback(async (data: Partial<Interview> & { questions?: Partial<InterviewQuestion>[] }): Promise<string> => {
    try {
      const result = await interviewsApi.create({
        title: data.title || '',
        description: data.description,
        estimated_duration_minutes: data.estimated_duration_minutes || 30,
        part_b_duration_minutes: data.part_b_duration_minutes ?? null,
        max_candidates: data.max_candidates,
        focus_areas: data.focus_areas,
        difficulty_level: data.difficulty_level || 'intermediate',
        language: data.language || 'en',
        ai_interviewer_name: data.ai_interviewer_name ?? null,
        instructions: data.instructions,
        status: data.status || 'draft',
        proctoring_enabled: data.proctoring_enabled,
        proctoring_config: data.proctoring_config,
        proctoring_require_consent: data.proctoring_require_consent,
        proctoring_evidence_mode: data.proctoring_evidence_mode,
        available_from: data.available_from,
        available_until: data.available_until,
        questions: data.questions,
      });

      setInterviews(prev => [result.interview, ...prev]);
      
      await safeAuditLog(
        'INTERVIEW_CREATED', 
        'interview',
        result.interview.id,
        {
          title: result.interview.title,
          difficulty_level: result.interview.difficulty_level
        }
      );

      return result.interview.id;
    } catch (error) {
      console.error('Error creating interview:', error);
      throw error;
    }
  }, []);

  const createCandidate = useCallback(async (data: Partial<Candidate>): Promise<CreateCandidateResult> => {
    try {
      const result = await usersApi.create({
        email: data.email || '',
        full_name: data.full_name || '',
        role: 'candidate',
        neptun_code: data.neptun_code
      });

      setCandidates(prev => [result.user, ...prev]);
      
      await safeAuditLog(
        'CANDIDATE_CREATED',
        'user',
        result.user.id,
        {
          email: data.email,
          full_name: data.full_name
        }
      );

      return {
        user: result.user,
        invitationToken: result.invitationToken,
        invitationExpiry: result.invitationExpiry,
        invitation_link: result.invitation_link
      };
    } catch (error) {
      console.error('Error creating candidate:', error);
      throw error;
    }
  }, []);

  const createInvigilator = useCallback(async (data: Partial<UserProfile>): Promise<CreateCandidateResult> => {
    try {
      const result = await usersApi.create({
        email: data.email || '',
        full_name: data.full_name || '',
        role: 'invigilator'
      });

      setInvigilators(prev => [result.user, ...prev]);

      await safeAuditLog(
        'INVIGILATOR_CREATED',
        'user',
        result.user.id,
        {
          email: data.email,
          full_name: data.full_name
        }
      );

      return {
        user: result.user,
        invitationToken: result.invitationToken,
        invitationExpiry: result.invitationExpiry,
        invitation_link: result.invitation_link
      };
    } catch (error) {
      console.error('Error creating invigilator:', error);
      throw error;
    }
  }, []);

  const assignInterview = useCallback(async (
    candidateId: string,
    interviewId: string,
    scheduledAt: string,
    interviewType: 'text' | 'voice' | 'mcq' = 'voice',
    allowRetake: boolean = false,
    maxAttempts: number = 1,
    completionDeadline?: string | null,
    candidateCanViewResults: boolean = false,
    autoApproveAssessment: boolean = false,
    accessPolicy?: {
      restriction_enabled?: boolean;
      allowed_ip_ranges?: string[];
      allowed_locations?: Array<{
        raw_input: string;
        city?: string | null;
        region?: string | null;
        country?: string | null;
        normalized_tokens?: string[];
      }>;
      match_mode?: 'any' | 'all';
      enforcement_mode?: 'start_only' | 'continuous';
      fail_action?: 'block' | 'warn';
    },
    proctoringPolicy?: {
      proctoring_enabled?: boolean;
      proctoring_config?: Record<string, any>;
    }
  ) => {
    try {
      const result = await interviewsApi.assign(
        interviewId,
        candidateId,
        scheduledAt,
        interviewType,
        allowRetake,
        maxAttempts,
        completionDeadline,
        candidateCanViewResults,
        autoApproveAssessment,
        accessPolicy,
        proctoringPolicy
      );
      const assigned = result.assignment || {};
      const interviewTitle = interviews.find((interview) => interview.id === interviewId)?.title;

      setCandidates(prev =>
        prev.map(candidate =>
          candidate.id === candidateId
            ? (() => {
                const normalizedCandidate = normalizeCandidateAssignments(candidate);
                const nextAssignment: CandidateAssignmentSummary = {
                  id: assigned.id || `${candidateId}:${assigned.interview_id || interviewId}`,
                  candidate_id: assigned.candidate_id || candidateId,
                  interview_id: assigned.interview_id || interviewId,
                  interview_scheduled_at: assigned.scheduled_at || scheduledAt,
                  interview_completion_deadline: assigned.completion_deadline || completionDeadline || undefined,
                  assignment_status: assigned.status || 'scheduled',
                  interview_type: assigned.interview_type || interviewType,
                  candidate_can_view_results:
                    typeof assigned.candidate_can_view_results === 'boolean'
                      ? assigned.candidate_can_view_results
                      : candidateCanViewResults,
                  auto_approve_assessment:
                    typeof assigned.auto_approve_assessment === 'boolean'
                      ? assigned.auto_approve_assessment
                      : autoApproveAssessment,
                  review_mode: assigned.review_mode || (autoApproveAssessment ? 'auto_approve' : 'manual'),
                  proctoring_enabled:
                    typeof assigned.proctoring_enabled === 'boolean'
                      ? assigned.proctoring_enabled
                      : Boolean(proctoringPolicy?.proctoring_enabled),
                  proctoring_config:
                    assigned.proctoring_config && typeof assigned.proctoring_config === 'object'
                      ? assigned.proctoring_config
                      : (proctoringPolicy?.proctoring_config || {}),
                  restriction_enabled:
                    typeof assigned.restriction_enabled === 'boolean'
                      ? assigned.restriction_enabled
                      : Boolean(accessPolicy?.restriction_enabled),
                  allowed_ip_ranges:
                    Array.isArray(assigned.allowed_ip_ranges)
                      ? assigned.allowed_ip_ranges
                      : (accessPolicy?.allowed_ip_ranges || []),
                  allowed_locations:
                    Array.isArray(assigned.allowed_locations)
                      ? assigned.allowed_locations
                      : (accessPolicy?.allowed_locations || []),
                  match_mode:
                    typeof assigned.match_mode === 'string'
                      ? assigned.match_mode
                      : (accessPolicy?.match_mode || 'any'),
                  enforcement_mode:
                    typeof assigned.enforcement_mode === 'string'
                      ? assigned.enforcement_mode
                      : (accessPolicy?.enforcement_mode || 'start_only'),
                  fail_action:
                    typeof assigned.fail_action === 'string'
                      ? assigned.fail_action
                      : (accessPolicy?.fail_action || 'block'),
                  allow_retake: typeof assigned.allow_retake === 'boolean' ? assigned.allow_retake : allowRetake,
                  max_attempts: Number.isFinite(assigned.max_attempts) ? assigned.max_attempts : Math.max(1, maxAttempts),
                  attempt_count: Number.isFinite(assigned.attempt_count) ? assigned.attempt_count : (normalizedCandidate.attempt_count || 0),
                  assignment_updated_at: assigned.updated_at || new Date().toISOString(),
                  interview_title: interviewTitle || normalizedCandidate.interview_title
                };

                const existingAssignments = [...(normalizedCandidate.candidate_assignments || [])];
                const existingIndex = existingAssignments.findIndex(
                  (entry) => entry.interview_id === nextAssignment.interview_id
                );

                if (existingIndex >= 0) {
                  existingAssignments[existingIndex] = {
                    ...existingAssignments[existingIndex],
                    ...nextAssignment
                  };
                } else {
                  existingAssignments.push(nextAssignment);
                }

                return normalizeCandidateAssignments({
                  ...normalizedCandidate,
                  candidate_assignments: existingAssignments
                });
              })()
            : candidate
        )
      );

      await safeAuditLog(
        'INTERVIEW_ASSIGNED',
        'candidate_assignment',
        undefined,
        {
          candidate_id: candidateId,
          interview_id: interviewId,
          scheduled_at: scheduledAt,
          completion_deadline: completionDeadline || null,
          interview_type: interviewType,
          candidate_can_view_results: candidateCanViewResults,
          auto_approve_assessment: autoApproveAssessment,
          restriction_enabled: Boolean(accessPolicy?.restriction_enabled),
          allowed_ip_ranges: accessPolicy?.allowed_ip_ranges || [],
          allowed_locations: accessPolicy?.allowed_locations || [],
          match_mode: accessPolicy?.match_mode || 'any',
          enforcement_mode: accessPolicy?.enforcement_mode || 'start_only',
          fail_action: accessPolicy?.fail_action || 'block',
          allow_retake: allowRetake,
          max_attempts: maxAttempts
        }
      );
    } catch (error) {
      console.error('Error assigning interview:', error);
      throw error;
    }
  }, []);

  const cancelInterviewAssignment = useCallback(async (candidateId: string, assignmentId: string) => {
    try {
      const result = await usersApi.cancelAssignment(candidateId, assignmentId);
      const cancelledInterviewId = result.assignment?.interview_id;

      setCandidates((prev) => prev.map((candidate) => {
        if (candidate.id !== candidateId) {
          return candidate;
        }

        const normalizedCandidate = normalizeCandidateAssignments(candidate);
        const remainingAssignments = (normalizedCandidate.candidate_assignments || []).filter((assignment) => (
          assignment.id !== assignmentId
          && assignment.interview_id !== cancelledInterviewId
        ));

        return normalizeCandidateAssignments({
          ...normalizedCandidate,
          candidate_assignments: remainingAssignments
        });
      }));
    } catch (error) {
      console.error('Error cancelling interview assignment:', error);
      throw error;
    }
  }, []);

  const assignInvigilator = useCallback(async (
    invigilatorId: string,
    candidateIds: string[],
    interviewId: string
  ): Promise<{ addedCount: number; skippedCandidates: string[] }> => {
    try {
      const result = await invigilationApi.assign(invigilatorId, candidateIds, interviewId);

      await safeAuditLog(
        'INVIGILATOR_ASSIGNED',
        'invigilator_assignment',
        undefined,
        {
          invigilator_id: invigilatorId,
          candidate_ids: candidateIds,
          interview_id: interviewId,
          added_count: result.added_count,
          skipped_candidates: result.skipped_candidates || []
        }
      );

      return {
        addedCount: result.added_count || 0,
        skippedCandidates: result.skipped_candidates || []
      };
    } catch (error) {
      console.error('Error assigning invigilator:', error);
      throw error;
    }
  }, []);

  const assignInvigilatorBySection = useCallback(async (
    invigilatorId: string,
    sectionId: string,
    interviewId: string
  ): Promise<{ addedCount: number; skippedCandidates: string[] }> => {
    try {
      const result = await invigilationApi.assignBySection(invigilatorId, sectionId, interviewId);

      await safeAuditLog(
        'INVIGILATOR_ASSIGNED_BY_SECTION',
        'candidate_section',
        sectionId,
        {
          invigilator_id: invigilatorId,
          section_id: sectionId,
          interview_id: interviewId,
          added_count: result.added_count,
          skipped_candidates: result.skipped_candidates || []
        }
      );

      return {
        addedCount: result.added_count || 0,
        skippedCandidates: result.skipped_candidates || []
      };
    } catch (error) {
      console.error('Error assigning invigilator by section:', error);
      throw error;
    }
  }, []);

  const createSection = useCallback(async (data: { name: string; description?: string }) => {
    try {
      const result = await sectionsApi.create(data);
      await refreshSections({ includeArchived: true });

      await safeAuditLog(
        'SECTION_CREATED',
        'candidate_section',
        result.section?.id,
        {
          name: result.section?.name || data.name,
          description: result.section?.description || data.description || null
        }
      );
    } catch (error) {
      console.error('Error creating section:', error);
      throw error;
    }
  }, [refreshSections]);

  const updateSection = useCallback(async (sectionId: string, data: { name?: string; description?: string; status?: 'active' | 'archived' }) => {
    try {
      await sectionsApi.update(sectionId, data);
      await refreshSections({ includeArchived: true });

      await safeAuditLog(
        'SECTION_UPDATED',
        'candidate_section',
        sectionId,
        data
      );
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  }, [refreshSections]);

  const deleteSection = useCallback(async (sectionId: string, options?: { archive?: boolean }) => {
    try {
      await sectionsApi.remove(sectionId, { archive: options?.archive !== false });
      await refreshSections({ includeArchived: true });

      await safeAuditLog(
        options?.archive === false ? 'SECTION_DELETED' : 'SECTION_ARCHIVED',
        'candidate_section',
        sectionId,
        { archive: options?.archive !== false }
      );
    } catch (error) {
      console.error('Error deleting section:', error);
      throw error;
    }
  }, [refreshSections]);

  const addSectionMembers = useCallback(async (sectionId: string, candidateIds: string[]) => {
    try {
      const result = await sectionsApi.addMembers(sectionId, candidateIds);
      await refreshSections({ includeArchived: true });
      await refreshUsers();

      await safeAuditLog(
        'SECTION_MEMBERS_ADDED',
        'candidate_section',
        sectionId,
        {
          requested_count: candidateIds.length,
          added_count: result.added_count
        }
      );
      return result;
    } catch (error) {
      console.error('Error adding section members:', error);
      throw error;
    }
  }, [refreshSections, refreshUsers]);

  const removeSectionMember = useCallback(async (sectionId: string, candidateId: string) => {
    try {
      await sectionsApi.removeMember(sectionId, candidateId);
      await refreshSections({ includeArchived: true });

      await safeAuditLog(
        'SECTION_MEMBER_REMOVED',
        'candidate_section',
        sectionId,
        { candidate_id: candidateId }
      );
    } catch (error) {
      console.error('Error removing section member:', error);
      throw error;
    }
  }, [refreshSections]);

  const assignInterviewToSection = useCallback(async (
    sectionId: string,
    data: {
      interviewId: string;
      scheduledAt?: string | null;
      completionDeadline?: string | null;
      candidateCanViewResults?: boolean;
      autoApproveAssessment?: boolean;
      reviewMode?: 'manual' | 'auto_approve' | 'auto_review';
      restrictionEnabled?: boolean;
      allowedIpRanges?: string[];
      allowedLocations?: Array<{
        raw_input: string;
        city?: string | null;
        region?: string | null;
        country?: string | null;
        normalized_tokens?: string[];
      }>;
      matchMode?: 'any' | 'all';
      enforcementMode?: 'start_only' | 'continuous';
      failAction?: 'block' | 'warn';
      interviewType?: 'text' | 'voice' | 'mcq';
      allowRetake?: boolean;
      maxAttempts?: number;
      retakeScope?: 'none' | 'part_a' | 'part_b' | 'both';
    }
  ) => {
    try {
      const result = await sectionsApi.assignInterview(sectionId, {
        interview_id: data.interviewId,
        scheduled_at: data.scheduledAt,
        completion_deadline: data.completionDeadline ?? null,
        candidate_can_view_results: data.candidateCanViewResults,
        auto_approve_assessment: data.autoApproveAssessment,
        review_mode: data.reviewMode || (data.autoApproveAssessment ? 'auto_approve' : 'manual'),
        restriction_enabled: data.restrictionEnabled,
        allowed_ip_ranges: data.allowedIpRanges,
        allowed_locations: data.allowedLocations,
        match_mode: data.matchMode,
        enforcement_mode: data.enforcementMode,
        fail_action: data.failAction,
        interview_type: data.interviewType,
        allow_retake: data.allowRetake,
        max_attempts: data.maxAttempts,
        retake_scope: data.retakeScope ?? (data.allowRetake ? 'both' : 'none')
      });
      await refreshSections({ includeArchived: true });
      await refreshUsers();

      await safeAuditLog(
        'SECTION_INTERVIEW_ASSIGNED',
        'section_assignment',
        undefined,
        {
          section_id: sectionId,
          interview_id: data.interviewId,
          scheduled_at: data.scheduledAt,
          completion_deadline: data.completionDeadline || null,
          candidate_can_view_results: data.candidateCanViewResults,
          auto_approve_assessment: data.autoApproveAssessment,
          restriction_enabled: data.restrictionEnabled,
          match_mode: data.matchMode,
          enforcement_mode: data.enforcementMode,
          fail_action: data.failAction,
          interview_type: data.interviewType,
          allow_retake: data.allowRetake,
          max_attempts: data.maxAttempts,
          retake_scope: data.retakeScope ?? (data.allowRetake ? 'both' : 'none'),
          assigned_candidates: result.assigned_candidates
        }
      );
    } catch (error) {
      console.error('Error assigning interview to section:', error);
      throw error;
    }
  }, [refreshSections, refreshUsers]);

  const removeSectionAssignment = useCallback(async (sectionId: string, assignmentId: string) => {
    try {
      await sectionsApi.removeAssignment(sectionId, assignmentId);
      await refreshSections({ includeArchived: true });

      await safeAuditLog(
        'SECTION_INTERVIEW_UNASSIGNED',
        'section_assignment',
        assignmentId,
        {
          section_id: sectionId,
          assignment_id: assignmentId
        }
      );
    } catch (error) {
      console.error('Error removing section assignment:', error);
      throw error;
    }
  }, [refreshSections]);

  const approveAssessment = useCallback(async (
    assessmentId: string,
    approved: boolean,
    modifiedScore?: number,
    comments?: string,
    scoreModificationReason?: string
  ) => {
    try {
      const result = await assessmentsApi.review(assessmentId, {
        admin_status: approved ? 'approved' : 'rejected',
        admin_final_score: modifiedScore,
        admin_score_modification_reason: scoreModificationReason,
        admin_comments: comments,
        admin_decision: approved ? 'proceed' : 'reject'
      });

      setAssessments(prev =>
        prev.map(assessment =>
          assessment.id === assessmentId
            ? result.assessment
            : assessment
        )
      );

      await safeAuditLog(
        'ASSESSMENT_REVIEWED',
        'assessment',
        assessmentId,
        {
          approved,
          modified_score: modifiedScore,
          score_modification_reason: scoreModificationReason,
          comments
        }
      );
    } catch (error) {
      console.error('Error approving assessment:', error);
      throw error;
    }
  }, []);

  const deleteInterview = useCallback(async (id: string) => {
    try {
      await interviewsApi.delete(id);
      setInterviews(prev => prev.filter(i => i.id !== id));
      
      await safeAuditLog('INTERVIEW_DELETED', 'interview', id, {});
    } catch (error) {
      const message = error instanceof Error ? error.message.toLowerCase() : '';
      if (message.includes('interview not found')) {
        setInterviews(prev => prev.filter(i => i.id !== id));
        return;
      }

      console.error('Error deleting interview:', error);
      throw error;
    }
  }, []);

  const updateInterview = useCallback(async (id: string, data: Partial<Interview>) => {
    try {
      const result = await interviewsApi.update(id, data);
      setInterviews(prev => prev.map(i => i.id === id ? result.interview : i));
      
      await safeAuditLog('INTERVIEW_UPDATED', 'interview', id, data);
    } catch (error) {
      console.error('Error updating interview:', error);
      throw error;
    }
  }, []);

  const deleteCandidate = useCallback(async (id: string) => {
    try {
      await usersApi.delete(id);
      setCandidates(prev => prev.filter(c => c.id !== id));
      
      await safeAuditLog('CANDIDATE_DELETED', 'user', id, {});
    } catch (error) {
      console.error('Error deleting candidate:', error);
      throw error;
    }
  }, []);

  const deleteInvigilator = useCallback(async (id: string) => {
    try {
      await usersApi.delete(id);
      setInvigilators(prev => prev.filter(user => user.id !== id));

      await safeAuditLog('INVIGILATOR_DELETED', 'user', id, {});
    } catch (error) {
      console.error('Error deleting invigilator:', error);
      throw error;
    }
  }, []);

  const value: AdminContextType = {
    interviews,
    candidates,
    invigilators,
    sections,
    assessments,
    loading,
    createInterview,
    createCandidate,
    createInvigilator,
    assignInterview,
    cancelInterviewAssignment,
    assignInvigilator,
    assignInvigilatorBySection,
    createSection,
    updateSection,
    deleteSection,
    refreshSections,
    addSectionMembers,
    removeSectionMember,
    assignInterviewToSection,
    removeSectionAssignment,
    approveAssessment,
    loadData,
    deleteInterview,
    updateInterview,
    deleteCandidate,
    deleteInvigilator,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}